import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, ArrowRight, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { getFamilyDefinition } from '../../content';
import { playSoundIntent } from '../../utils/sound/soundIntent';
import { hapticManager } from '../../utils/hapticManager';
import { CenteredModal } from '../shared/CenteredModal';
import { MODE_THEME } from '../shared/modeTheme';
import { getMasteryLabelInfo } from '../shared/masteryLabelInfo';
interface ChallengeAcceptModalProps {
  isOpen: boolean;
  familyId: string;
  masteryScore: number;
  onAccept: () => void;
  onDecline: () => void;
}
/**
 * ChallengeAcceptModal — Commitment modal before starting a challenge session.
 *
 * Shows the student their current mastery level and the challenge tier they'll face.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function ChallengeAcceptModal({
  isOpen,
  familyId,
  masteryScore,
  onAccept,
  onDecline
}: ChallengeAcceptModalProps) {
  const family = getFamilyDefinition(familyId);
  const challengeTheme = MODE_THEME['challenge'];
  if (!isOpen || !family) return null;
  // Map mastery score to the 16-tier system: mastery 70% ≈ tier 11, 80% ≈ tier 13
  const currentTier = Math.min(16, Math.max(1, Math.round(masteryScore / 100 * 16)));
  const challengeTier = Math.min(16, currentTier + 2);
  // Map challengeTier (1-16) to 1-5 stars
  const getStars = (tier: number) => {
    if (tier <= 3) return '⭐';
    if (tier <= 6) return '⭐⭐';
    if (tier <= 9) return '⭐⭐⭐';
    if (tier <= 12) return '⭐⭐⭐⭐';
    return '⭐⭐⭐⭐⭐';
  };
  return <CenteredModal isOpen={isOpen} onClose={onDecline} maxWidth="sm" ariaLabel="Uitdaging aannemen" panelClassName={`bg-white shadow-2xl border-2 ${challengeTheme.borderSoft} rounded-3xl overflow-hidden`} backdropClassName="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" springBounce={0.3} springDuration={0.28} data-id="element-3094" data-mp="1sjkmhq8r6a9w">
      
      {/* Header gradient */}
      <div className={`bg-gradient-to-br ${challengeTheme.gradient} p-6 text-white relative overflow-hidden`} data-id="element-3095" data-mp="1kriv9ncvawkm">
        
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" data-id="element-3096" data-mp="1osmemju4jaql" />
        <button onClick={onDecline} className="absolute top-2 right-2 w-11 h-11 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors" aria-label="Sluiten" data-id="element-3097" data-mp="1tl5nx4ls3jpl">
          
          <X className="w-5 h-5 text-white" data-id="element-3098" data-mp="11u9c87bgfl3w" />
        </button>

        {/* Kwam binnen vanuit scale(0): iets dat uit niets tevoorschijn komt. Nu
             vanaf 0.96 met opacity, zoals de rest van de app. */}
        <motion.div initial={{
        scale: 0.96,
        opacity: 0,
        rotate: -8
      }} animate={{
        scale: 1,
        opacity: 1,
        rotate: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1],
        delay: 0.1
      }} className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4" data-id="element-3099" data-mp="1as1unkch38vi">
          
          <Trophy className="w-9 h-9 text-white" strokeWidth={2.5} data-id="element-3100" data-mp="fjhpko1h45z6" />
        </motion.div>

        <h2 className="text-xl font-black leading-tight mb-1" data-id="element-3101" data-mp="alolc0jqdm1j">
          Klaar voor de uitdaging?
        </h2>
        <p className="text-white/80 text-sm font-medium" data-id="element-3102" data-mp="5sn3pks538e9">
          {family.description}
        </p>
      </div>

      {/* Body */}
      <div className="p-6 space-y-4" data-id="element-3103" data-mp="3sz5y59rbgd2v">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3" data-id="element-3104" data-mp="3t0qp6pef9dvz">
          {/* Dit was het grootste getal op het scherm: "62%" onder "Jouw
               niveau", pal naast de sterren. Een kind dat op het punt staat een
               uitdaging aan te gaan, leest daar een cijfer over zichzelf — precies
               het vergelijkingsgetal dat het beleid uitsluit. Het label zegt wat
               het kind moet weten: dat het hier klaar voor is. */}
          <div className="bg-slate-50 rounded-2xl p-3 text-center border border-slate-100" data-id="element-3105" data-mp="1caii5cgmb1bm">
            <div className="text-lg font-black text-slate-700 leading-tight" data-id="element-3106" data-mp="1kb833o8rfx8k">
              {getMasteryLabelInfo(masteryScore).label}
            </div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mt-0.5" data-id="element-3107" data-mp="1p8zwkmp1zrev">
              Jouw niveau
            </div>
          </div>
          <div className={`${challengeTheme.bgSoft} rounded-2xl p-3 text-center border ${challengeTheme.borderSoft}`} data-id="element-3108" data-mp="1fc5009ccc138">
            
            <div className={`text-2xl font-black ${challengeTheme.textDark}`} data-id="element-3109" data-mp="11jp1nludwr8n">
              {getStars(challengeTier)}
            </div>
            <div className={`text-xs font-bold ${challengeTheme.text} uppercase tracking-wide mt-0.5 opacity-70`} data-id="element-3110" data-mp="16dji8gep0q3i">
              
              Hoe moeilijk?
            </div>
          </div>
        </div>

        {/* Message */}
        <p className="text-sm text-slate-600 font-medium text-center leading-relaxed" data-id="element-3111" data-mp="im4bmbfvvmmg">
          Dit wordt moeilijker dan je gewend bent — maar jij kunt het! 💪
        </p>

        {/* Actions */}
        <div className="space-y-2 pt-1" data-id="element-3112" data-mp="3yvvcw7pwr3m">
          <Button onClick={() => {
          playSoundIntent('reward.levelUp');
          hapticManager.hapticMedium();
          onAccept();
        }} variant="primary" size="lg" fullWidth className={`bg-gradient-to-r ${challengeTheme.gradient} border-none shadow-lg ${challengeTheme.shadow} rounded-2xl font-black hover:brightness-110`} data-id="element-3113" data-mp="3zykozpe6lhf">
            
            Start Uitdaging <ArrowRight className="w-4 h-4 ml-2" data-id="element-3114" data-mp="mxhva7ri0c5y" />
          </Button>
          <Button onClick={onDecline} variant="secondary" size="lg" fullWidth className="rounded-2xl" data-id="element-3115" data-mp="mvoqky0g420l">
            
            Kies iets anders
          </Button>
        </div>
      </div>
    </CenteredModal>;
}